import { useState, useEffect } from 'react';
import { X, FileText, Bookmark } from 'lucide-react';
import { Post } from '../types';
import { getAuthorInfo, getPostContent } from '../lib/postUtils';
import { updatePostNote } from '../services/api';
import { TikTokIcon } from './TikTokIcon';
import { RedditIcon } from './RedditIcon';

interface NoteModalProps {
    post: Post;
    isOpen: boolean;
    onClose: () => void;
    onNoteUpdate: (note: string) => void;
    onNoteDelete?: (postId: string, wordCount: number) => void;
}

export const NoteModal = ({ post, isOpen, onClose, onNoteUpdate, onNoteDelete }: NoteModalProps) => {
    const [note, setNote] = useState(post.note || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setNote(post.note || '');
            setError('');
        }
    }, [isOpen, post.note]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setIsSubmitting(true);
        setError('');

        try {
            await updatePostNote(post.id, note.trim());
            onNoteUpdate(note.trim());
            onClose();
        } catch (err) {
            setError('Failed to save note. Please try again.');
            console.error('Error updating note:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteNote = async () => {
        if (!post.note) return;

        setIsSubmitting(true);
        setError('');

        try {
            const wordCount = post.note.split(/\s+/).filter(word => word.length > 0).length;
            await updatePostNote(post.id, '');
            onNoteUpdate('');
            onNoteDelete?.(post.id.toString(), wordCount);
            onClose();
        } catch (err) {
            setError('Failed to delete note. Please try again.');
            console.error('Error deleting note:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const authorInfo = getAuthorInfo(post);
    const content = getPostContent(post) || '';

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2">
                        <FileText className="w-6 h-6 text-purple-400" />
                        <h2 className="text-xl font-semibold text-white">
                            {post.note ? 'Edit Note' : 'Add Note'}
                        </h2>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="text-gray-400 hover:text-white bg-transparent transition-colors disabled:opacity-50"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Author Info */}
                <div className='mb-4 py-4 px-6 bg-gray-700/50 rounded-lg'>
                    {authorInfo && (
                        <div className="flex items-center space-x-3 ">
                            <img
                                src={authorInfo.avatar}
                                alt={authorInfo.name}
                                className="w-12 h-12 rounded-full bg-gray-600"
                                onError={(e) => {
                                    e.currentTarget.src = "/assets/placeholder.jpg";
                                }}
                            />
                            <div>
                                <div className="font-semibold text-white">{authorInfo.name}</div>
                                <div className="text-sm text-gray-400">{authorInfo.handle}</div>
                            </div>
                            <div className="flex-1 flex justify-end">
                                {post.platform === 'INSTAGRAM' ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-pink-400">
                                        <rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect>
                                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                                        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>
                                    </svg>
                                ) : post.platform === 'LINKEDIN' ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
                                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                                        <rect width="4" height="12" x="2" y="9"></rect>
                                        <circle cx="4" cy="4" r="2"></circle>
                                    </svg>
                                ) : post.platform === 'TIKTOK' ? (
                                    <TikTokIcon className="w-5 h-5 text-white" />
                                ) : post.platform === 'REDDIT' ? (
                                    <RedditIcon className="w-5 h-5 text-[#FF4500]" />
                                ) : post.platform === 'CHROME_BOOKMARK' ? (
                                    <Bookmark className="w-5 h-5" />
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
                                        <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                                    </svg>
                                )}
                            </div>
                        </div>


                    )}

                    <div className="text-white text-sm leading-relaxed mt-4">
                        {content}

                    </div>

                </div>

                {/* Note Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="note" className="block text-sm font-medium text-gray-300 mb-2">
                            Your Note
                        </label>
                        <div>
                            <textarea
                                id="note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Add your insights, thoughts, or notes about this post..."
                                className="w-full h-32 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none"
                                disabled={isSubmitting}
                                maxLength={1000}
                            />
                            <div className="flex justify-end mt-1">
                                <span className="text-xs text-gray-400">
                                    {note.length} / 1000
                                </span>
                            </div>
                        </div>

                    </div>

                    {error && (
                        <div className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end space-x-3 pt-4">
                        {post.note && (
                            <button
                                type="button"
                                onClick={handleDeleteNote}
                                disabled={isSubmitting}
                                className="px-4 py-2 text-red-400 bg-red-400/20 hover:text-red-300 border border-red-600 hover:border-red-500 rounded-lg transition-colors disabled:opacity-50"
                            >
                                Delete Note
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-800 disabled:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <FileText className="w-4 h-4" />
                            )}
                            <span>{isSubmitting ? 'Saving...' : 'Save Note'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
